/**
 * Professional PDF Exporter for Affogato Documents
 * Uses pdfmake for reliable, vector-based PDF generation
 */

import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import type { TDocumentDefinitions, Content, ContentTable } from 'pdfmake/interfaces';

// Initialize pdfmake fonts
pdfMake.vfs = pdfFonts.vfs;

interface GeneratePdfOptions {
  title: string;
  markdown: string;
  documentType?: 'proposal' | 'architecture' | 'diagnostic' | 'general';
  locale?: 'es' | 'en';
}

// Format date based on locale
function formatDate(locale: 'es' | 'en'): string {
  const now = new Date();
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  return now.toLocaleDateString(locale === 'es' ? 'es-ES' : 'en-US', options);
}

// Get document type label
function getDocumentTypeLabel(type: string, locale: 'es' | 'en'): string {
  const labels: Record<string, Record<string, string>> = {
    proposal: { es: 'Propuesta Comercial', en: 'Commercial Proposal' },
    architecture: { es: 'Arquitectura de Solución', en: 'Solution Architecture' },
    diagnostic: { es: 'Diagnóstico de Madurez', en: 'Maturity Diagnostic' },
    general: { es: 'Documento', en: 'Document' },
  };
  return labels[type]?.[locale] || labels.general[locale];
}

// Get branding text
function getBrandingText(locale: 'es' | 'en'): string {
  return locale === 'es'
    ? 'Affogato - Cloud Storage para Consultores'
    : 'Affogato - Cloud Storage for Consultants';
}

// Extract title from markdown or filename
function extractTitle(title: string, markdown: string): string {
  const h1Match = markdown.match(/^#\s+(.+)$/m);
  if (h1Match && h1Match[1]) {
    return h1Match[1].replace(/\*\*/g, '').trim();
  }
  return title
    .replace(/\.md$/i, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Clean text from markdown formatting
function cleanText(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1');
}

// Parse markdown table to pdfmake table
function parseTable(lines: string[]): ContentTable | null {
  if (lines.length < 3) return null;

  const parseRow = (line: string): string[] => {
    const cells = line.split('|').map(cell => cell.trim());
    // Remove empty first and last cells (from leading/trailing |)
    if (cells[0] === '') cells.shift();
    if (cells[cells.length - 1] === '') cells.pop();
    return cells;
  };

  const headerRow = parseRow(lines[0]);
  if (headerRow.length === 0) return null;

  // Skip separator line (index 1)
  const dataRows = lines.slice(2)
    .filter(line => line.includes('|') && !line.match(/^[\s|:-]+$/))
    .map(parseRow)
    .filter(row => row.length > 0);

  if (dataRows.length === 0) return null;

  const body: any[][] = [
    headerRow.map(cell => ({
      text: cleanText(cell),
      bold: true,
      fillColor: '#f3f4f6'
    })),
    ...dataRows.map(row =>
      row.map(cell => ({
        text: cleanText(cell)
      }))
    )
  ];

  return {
    table: {
      headerRows: 1,
      widths: Array(headerRow.length).fill('*'),
      body
    },
    layout: 'lightHorizontalLines',
    margin: [0, 10, 0, 15] as [number, number, number, number]
  };
}

// Parse markdown to pdfmake content - simplified and safe
function parseMarkdown(markdown: string): Content[] {
  const content: Content[] = [];
  const lines = markdown.split('\n');

  let i = 0;
  const maxIterations = lines.length * 2; // Safety limit
  let iterations = 0;

  while (i < lines.length && iterations < maxIterations) {
    iterations++;
    const line = lines[i];

    // Skip empty lines
    if (!line || line.trim() === '') {
      i++;
      continue;
    }

    // H1
    if (line.startsWith('# ')) {
      content.push({ text: cleanText(line.slice(2)), style: 'h1' });
      i++;
      continue;
    }

    // H2
    if (line.startsWith('## ')) {
      content.push({ text: cleanText(line.slice(3)), style: 'h2' });
      i++;
      continue;
    }

    // H3
    if (line.startsWith('### ')) {
      content.push({ text: cleanText(line.slice(4)), style: 'h3' });
      i++;
      continue;
    }

    // H4
    if (line.startsWith('#### ')) {
      content.push({ text: cleanText(line.slice(5)), style: 'h4' });
      i++;
      continue;
    }

    // Horizontal rule
    if (line.match(/^[-*_]{3,}\s*$/)) {
      content.push({
        canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: '#e5e7eb' }],
        margin: [0, 10, 0, 10] as [number, number, number, number]
      });
      i++;
      continue;
    }

    // Table
    if (line.trim().startsWith('|')) {
      const tableLines: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i]);
        i++;
      }
      const table = parseTable(tableLines);
      if (table) {
        content.push(table);
      }
      continue;
    }

    // Unordered list
    if (line.match(/^[-*+]\s/)) {
      const listItems: string[] = [];
      while (i < lines.length && lines[i].match(/^[-*+]\s/)) {
        listItems.push(cleanText(lines[i].replace(/^[-*+]\s/, '')));
        i++;
      }
      content.push({
        ul: listItems.map(item => ({ text: item, style: 'listItem' })),
        margin: [0, 5, 0, 8] as [number, number, number, number]
      });
      continue;
    }

    // Ordered list
    if (line.match(/^\d+\.\s/)) {
      const listItems: string[] = [];
      while (i < lines.length && lines[i].match(/^\d+\.\s/)) {
        listItems.push(cleanText(lines[i].replace(/^\d+\.\s/, '')));
        i++;
      }
      content.push({
        ol: listItems.map(item => ({ text: item, style: 'listItem' })),
        margin: [0, 5, 0, 8] as [number, number, number, number]
      });
      continue;
    }

    // Blockquote
    if (line.startsWith('>')) {
      const quoteLines: string[] = [];
      while (i < lines.length && lines[i].startsWith('>')) {
        quoteLines.push(lines[i].replace(/^>\s*/, ''));
        i++;
      }
      content.push({
        text: cleanText(quoteLines.join(' ')),
        style: 'blockquote',
        margin: [20, 10, 20, 10] as [number, number, number, number]
      });
      continue;
    }

    // Code block
    if (line.startsWith('```')) {
      const codeLines: string[] = [];
      i++; // Skip opening
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      if (i < lines.length) i++; // Skip closing if found
      content.push({
        text: codeLines.join('\n'),
        style: 'code',
        background: '#f3f4f6',
        margin: [0, 10, 0, 10] as [number, number, number, number]
      });
      continue;
    }

    // Regular paragraph
    content.push({ text: cleanText(line), style: 'paragraph' });
    i++;
  }

  return content;
}

/**
 * Main export function - generates and downloads the PDF
 */
export async function generateProfessionalPdf(options: GeneratePdfOptions): Promise<void> {
  const { title, markdown, documentType = 'general', locale = 'es' } = options;

  const cleanTitle = extractTitle(title, markdown);
  const typeLabel = getDocumentTypeLabel(documentType, locale);
  const date = formatDate(locale);
  const branding = getBrandingText(locale);

  const bodyContent = parseMarkdown(markdown);

  const docDefinition: TDocumentDefinitions = {
    pageSize: 'A4',
    pageMargins: [40, 40, 40, 60],

    content: [
      // Cover page
      { text: '', margin: [0, 150, 0, 0] as [number, number, number, number] },
      { text: cleanTitle, style: 'coverTitle', alignment: 'center' },
      { text: typeLabel, style: 'coverSubtitle', alignment: 'center', margin: [0, 20, 0, 0] as [number, number, number, number] },
      { text: date, style: 'coverDate', alignment: 'center', margin: [0, 30, 0, 0] as [number, number, number, number] },
      { text: branding, style: 'coverBranding', alignment: 'center', margin: [0, 250, 0, 0] as [number, number, number, number] },
      { text: '', pageBreak: 'after' },

      // Body content
      ...bodyContent
    ],

    styles: {
      coverTitle: { fontSize: 24, bold: true, color: '#1f2937' },
      coverSubtitle: { fontSize: 14, color: '#6b7280' },
      coverDate: { fontSize: 11, color: '#9ca3af' },
      coverBranding: { fontSize: 9, color: '#d1d5db' },
      h1: { fontSize: 18, bold: true, color: '#1f2937', margin: [0, 20, 0, 10] },
      h2: { fontSize: 15, bold: true, color: '#1f2937', margin: [0, 15, 0, 8] },
      h3: { fontSize: 13, bold: true, color: '#374151', margin: [0, 12, 0, 6] },
      h4: { fontSize: 11, bold: true, color: '#4b5563', margin: [0, 10, 0, 5] },
      paragraph: { fontSize: 10, color: '#374151', lineHeight: 1.5, margin: [0, 0, 0, 8], alignment: 'justify' },
      listItem: { fontSize: 10, color: '#374151', lineHeight: 1.5 },
      blockquote: { fontSize: 10, italics: true, color: '#6b7280' },
      code: { fontSize: 9, color: '#1f2937' }
    },

    defaultStyle: {
      fontSize: 10,
      color: '#374151'
    },

    footer: (currentPage: number, pageCount: number) => {
      if (currentPage === 1) return { text: '' };
      return {
        text: `${currentPage} / ${pageCount}`,
        alignment: 'center' as const,
        fontSize: 9,
        color: '#9ca3af',
        margin: [0, 20, 0, 0]
      };
    }
  };

  const fileName = title.replace(/\.md$/i, '').replace(/[^a-zA-Z0-9\s-]/g, '').trim() + '.pdf';

  return new Promise<void>((resolve, reject) => {
    setTimeout(() => {
      try {
        const pdfDoc = pdfMake.createPdf(docDefinition);
        pdfDoc.download(fileName);
        // Resolve after short delay - download triggers synchronously
        setTimeout(() => resolve(), 300);
      } catch (err) {
        console.error('[PdfExporter] Error:', err);
        reject(err);
      }
    }, 50);
  });
}

/**
 * Helper to detect document type from title
 */
export function detectDocumentType(title: string): 'proposal' | 'architecture' | 'diagnostic' | 'general' {
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes('propuesta') || lowerTitle.includes('proposal')) {
    return 'proposal';
  }
  if (lowerTitle.includes('arquitectura') || lowerTitle.includes('architecture')) {
    return 'architecture';
  }
  if (lowerTitle.includes('diagnóstico') || lowerTitle.includes('diagnostic') || lowerTitle.includes('madurez') || lowerTitle.includes('maturity')) {
    return 'diagnostic';
  }
  return 'general';
}
